"use client";

import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import baseURL from "../../config/config";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import axiosInstance from "../../helpers/Axios";

// ===== STYLED COMPONENTS =====
const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 30vh;
  padding: 2rem;
  background-color: #f3f4f6;
  margin-top: 5rem;
`;

const FormContainer = styled(Box)`
  background-color: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 28rem;
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 0.75rem;
    margin: 1rem;
  }
`;

const ResultCard = styled(Box)`
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  background-color: #2563eb;
  &:hover {
    background-color: #1d4ed8;
  }
`;

// ===== MAIN COMPONENT =====
const GenerateTimedTranscript = () => {
  const [excelFile, setExcelFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [open, setOpen] = useState(false);

  const handleFileChange = (e) => setExcelFile(e.target.files[0]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!excelFile) {
      alert("Please upload an Excel file (.xlsx or .xls).");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", excelFile);

    try {
      const response = await axiosInstance.post(
        `${baseURL}/api/trail/generate-timed-transcript/`,
        formData
      );

      // ✅ Fix: unwrap nested data structure
      const payload = response?.data?.data || response?.data;
      console.log("Raw Response:", response.data);
      console.log("Unwrapped Payload:", payload);

      setResponseData(payload);
    } catch (error) {
      console.error("Error uploading file:", error);
      setResponseData({
        error:
          error.response?.data?.error ||
          error.response?.data?.detail ||
          "Upload failed! Please try again.",
      });
    } finally {
      setLoading(false);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setExcelFile(null);
    setResponseData(null);
  };

  // ===== RENDER UPLOAD RESULTS =====
  const renderResults = () => {
    const processed = responseData?.processed || [];
    const failed = responseData?.failed || [];

    if (!processed.length && !failed.length) return null;

    return (
      <div className="mt-6 border border-gray-200 p-3 rounded-lg">
        <Typography variant="h6" className="mb-3 text-gray-800 font-medium">
          Detailed Results
        </Typography>
        <div className="max-h-80 overflow-y-auto pr-1">
          {processed.map((item, index) => {
            const isSuccess = item.status === "success";
            const isExists = item.status === "already_exists";
            const colorScheme = isSuccess
              ? { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", icon: <CheckCircleIcon /> }
              : isExists
              ? { bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-700", icon: <AutorenewIcon /> }
              : { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", icon: <ErrorIcon /> };

            return (
              <div
                key={index}
                className={`${colorScheme.bg} p-3 rounded-lg mb-2 border ${colorScheme.border}`}
              >
                <div className="flex items-center space-x-2">
                  {colorScheme.icon}
                  <Typography variant="body1" className={`font-semibold ${colorScheme.text}`}>
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    className={`ml-auto text-xs px-2 py-1 rounded ${colorScheme.bg} ${colorScheme.text}`}
                  >
                    {item.status.toUpperCase()}
                  </Typography>
                </div>
              </div>
            );
          })}

          {failed.map((item, index) => (
            <div
              key={index}
              className="bg-red-50 border border-red-200 p-3 rounded-lg mb-2"
            >
              <div className="flex items-center space-x-2 text-red-700">
                <ErrorIcon />
                <Typography variant="body1" className="font-semibold">
                  {item.title}
                </Typography>
              </div>
              <Typography variant="body2" className="text-red-600 ml-6">
                {item.error}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ===== RENDER UI =====
  return (
    <StyledBox>
      <FormContainer>
        <Typography variant="h4" className="text-center mb-6 font-semibold text-gray-800">
          Generate Timed Transcript
        </Typography>

        <form onSubmit={handleSubmit} className="space-y-6">
          <TextField
            type="file"
            label="Excel File"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <DescriptionIcon className="w-5 h-5 text-gray-400 mr-2" />
              ),
            }}
          />

          <StyledButton
            type="submit"
            variant="contained"
            fullWidth
            className="py-2.5"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <>
                <AutorenewIcon className="w-5 h-5 mr-2" /> Generate Transcript
              </>
            )}
          </StyledButton>
        </form>
      </FormContainer>

      <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <div className="relative">
          <div className="px-6 py-4 border-b">
            <div className="flex justify-between items-center">
              <Typography variant="h6" className="text-gray-800">
                Transcript Generation Results
              </Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          <DialogContent className="px-6 py-5">
            {responseData ? (
              responseData.error ? (
                <div className="bg-red-50 text-red-700 p-4 rounded-lg">
                  <ErrorIcon className="mr-2" />
                  {responseData.error}
                </div>
              ) : (
                <div className="space-y-4">
                  <div
                    className={`flex items-center space-x-3 p-4 rounded-lg ${
                      responseData.failed?.length
                        ? "bg-yellow-50 text-yellow-700"
                        : "bg-green-50 text-green-700"
                    }`}
                  >
                    {responseData.failed?.length ? (
                      <AutorenewIcon className="h-5 w-5" />
                    ) : (
                      <CheckCircleIcon className="h-5 w-5" />
                    )}
                    <p className="font-medium">{responseData.message}</p>
                  </div>

                  {responseData.summary && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <ResultCard>
                        <Typography variant="subtitle2" className="text-gray-500">
                          Total
                        </Typography>
                        <Typography variant="h6" className="text-gray-900">
                          {responseData.summary.total}
                        </Typography>
                      </ResultCard>
                      <ResultCard>
                        <Typography variant="subtitle2" className="text-gray-500">
                          Success
                        </Typography>
                        <Typography variant="h6" className="text-green-600">
                          {responseData.summary.success}
                        </Typography>
                      </ResultCard>
                      <ResultCard>
                        <Typography variant="subtitle2" className="text-gray-500">
                          Already Exists
                        </Typography>
                        <Typography variant="h6" className="text-yellow-600">
                          {responseData.summary.already_exists}
                        </Typography>
                      </ResultCard>
                      <ResultCard>
                        <Typography variant="subtitle2" className="text-gray-500">
                          Failed
                        </Typography>
                        <Typography variant="h6" className="text-red-600">
                          {responseData.summary.failed}
                        </Typography>
                      </ResultCard>
                    </div>
                  )}
                  {renderResults()}
                </div>
              )
            ) : (
              <Typography className="text-gray-600">No data available.</Typography>
            )}
          </DialogContent>

          <div className="border-t px-6 py-4 bg-gray-50">
            <div className="flex justify-end">
              <StyledButton onClick={handleClose} variant="contained">
                Close
              </StyledButton>
            </div>
          </div>
        </div>
      </StyledDialog>
    </StyledBox>
  );
};

export default GenerateTimedTranscript;

